import { groupBy, reduce, sumBy } from 'lodash';
import * as React from 'react';
import { CartSeat, Seat } from '../../../classes/seat';
import { convertStringToStringList } from '../../../helpers/array-helpers';
import { getCurrencyFormat } from '../../../helpers/currency-format';
import { selectGroupedSeats, selectSeat, closeSeatmapCart } from '../../../seatmap/actions/seatmap-actions';
import { removeSeatFromCart, saveSeatRemoval } from '../../../cart/actions/cart-actions';
import LineItem from './line-item';
import { PriceType } from '../../../classes/price-type';

export interface ICartItemsProps {
    cartSeats: Array<CartSeat>;
    dispatch: any /*Dispatch<any> can't interpret react-redux dispatch method and react-redux typings exports Redux.Dispatch.*/;
    itemId: number;
    seatMapId: string;
    selectedSeatIds: string;
    allSeats: Array<Seat>;
    selectedPriceTypes: Array<PriceType>;

    cartSubtotalLabel: string;
    cartEmptyNotice: string;
    useZoneDescription: boolean;
    groupSeatCartItemText: string;
    currencyFormat: string;
    exchangeFreeLabel: string;
    brandId: string;
}

// List of cart items (LineItem components) and subtotal
class CartItems extends React.PureComponent<ICartItemsProps, { seatToRemove: CartSeat }> {
    render() {
        const { cartSeats } = this.props;

        return (

            <ul className="cart-container-inner">
                {cartSeats.length > 0 ?
                    this.getLineItems(cartSeats)

                    : <li className="cart-item"><p className="caption-paragraph">{this.props.cartEmptyNotice}</p></li>}
            </ul>

        );
    }

    getIsSelected(seat: CartSeat) {
        return convertStringToStringList(this.props.selectedSeatIds).some(selectedSeatId => selectedSeatId === seat.seatId);
    }

    // Takes in array of seats and outputs an array of LineItem components: first single seats, then group seats
    getLineItems(seats: Array<CartSeat>): Array<JSX.Element> {
        const seatsByGrouped = groupBy(seats, (seat) => seat.isGroupedSeat ? 'group' : 'single');
        const singleSeats = seatsByGrouped['single'] || [];
        const groupSeats = seatsByGrouped['group'] || [];

        const singleLineItems = this.getSingleLineItems(singleSeats);
        const groupLineItems = this.getGroupLineItems(groupSeats);
        return singleLineItems.concat(groupLineItems);
    }

    // Takes in array of single seats and ouputs an array of LineItem components
    getSingleLineItems(seats: Array<CartSeat>): Array<JSX.Element> {
        return seats.map(seat =>
            <LineItem key={seat.tessituraSeat.seatNumber.toString()}
                description={this.getSingleSeatDescription(seat)}
                isSelected={this.getIsSelected(seat)}
                onSelect={() => this._onCartItemClick(seat)}
                onRemove={() => this._onRemoveCartItemClick(seat)}
                priceTypeDescription={seat.selectedPricing.priceTypeDescription}
                price={this.cartItemPriceText(seat.selectedPricing.priceCarted)}
            />
        );
    }

    // LineItem description for a single seat
    getSingleSeatDescription(seat: CartSeat) {
        return `${this.getZoneOrSection(seat)} ${seat.tessituraSeat.rowText} - ${seat.tessituraSeat.numberText}`;
    }

    // Zone text or section text, depending on app setting
    getZoneOrSection(seat: CartSeat) {
        var useZoneDescription = (this.props.useZoneDescription && seat.zoneDescription !== null);
        return useZoneDescription ? seat.zoneDescription : seat.tessituraSeat.sectionDescription;
    }

    // Takes in array of group seats, groups by svgPathId, outputs array of LineItem components
    getGroupLineItems(seats: Array<CartSeat>): Array<JSX.Element> {
        let lineItems: Array<JSX.Element> = [];
        const groupSeatsByPath = groupBy(seats, (seat) => seat.svgPathId);

        for (var pathId in groupSeatsByPath) {
            const groupedSeats = groupSeatsByPath[pathId];
            const section = groupedSeats[0].tessituraSeat.sectionDescription;
            const totalPriceText = this.cartItemPriceText(sumBy(groupedSeats, (seat) => seat.selectedPricing.priceCarted));
            const priceTypeDescription = groupedSeats[0].selectedPricing.priceTypeDescription;

            const allCartSeatsInSamePath = this.props.allSeats.filter(allSeat => allSeat.svgPathId === pathId);
            const onCartItemsClick = () => this._onCartItemsClick(allCartSeatsInSamePath.map(allSeat => allSeat as CartSeat));

            lineItems.push(
                <LineItem key={`groupseat-${pathId}`}
                    description={this.getGroupLineItemDescription(groupedSeats.length, section)}
                    isSelected={this.getIsSelected(groupedSeats[0])}
                    onSelect={onCartItemsClick}
                    onRemove={() => { this._onRemoveCartItemsClick(groupedSeats) }}
                    price={totalPriceText}
                    priceTypeDescription={priceTypeDescription}
                />
            );
        }

        return lineItems;
    }

    getGroupLineItemDescription(number: number, section: string) {
        const groupSeatText = this.props.groupSeatCartItemText;
        return groupSeatText.replace("{0}", number.toString()).replace("{1}", section);
    }

    _onRemoveCartItemClick(cartItem: CartSeat) {
        this.props.dispatch(saveSeatRemoval(cartItem))
        this.props.dispatch(removeSeatFromCart(cartItem, this.props.selectedPriceTypes, this.props.brandId, false));
    }

    _onRemoveCartItemsClick(cartItems: Array<CartSeat>) {
        cartItems.forEach(item =>
            this.props.dispatch(removeSeatFromCart(item, this.props.selectedPriceTypes, this.props.brandId, false))
        );
    }

    _onCartItemClick(cartItem: CartSeat) {
        // Only select seat if on same level
        if (this.props.allSeats.filter(s => s.seatId == cartItem.seatId).length > 0) {
            this.props.dispatch(selectSeat(cartItem, this.props.seatMapId));
            this.props.dispatch(closeSeatmapCart(false));
        }
    }

    // Click handler for a group seat LineItem
    _onCartItemsClick(cartItems: Array<CartSeat>) {
        // Assumes view for the first seat in the array
        this.props.dispatch(selectGroupedSeats(cartItems, this.props.seatMapId));
    }

    cartItemPriceText(price: number): string {
        if (price > 0 || this.props.exchangeFreeLabel === "") {
            return getCurrencyFormat(this.props.currencyFormat, price);
        }

        return this.props.exchangeFreeLabel;
    }

}

export default CartItems;